import './default.scss';

import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { Warnings } from '@/constants/Warnings';
import ListProductsLoad from '@/resources/components/loads/ListProductsLoad';
import FreeShippingInLine from '@/resources/components/product/freeShippingAlert/FreeShippingInLine';
import ImagePreview from '@/resources/components/product/imagePreview/ImagePreview';
import { useDevice } from '@/resources/hooks/useDevice';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { getProductByCombo } from '@/routes/products';
import { IFullProduct, IProduct } from '@/store/ducks/products/types';

import productNotFound from '../../../assets/images/product_not_found.png';
import ListSelectedOptions from '../../../components/cart/listSelectedOptions/ListSelectedOptions';
import QuantityAlert from '../../../components/global/badges/QuantityAlert';
import Breadcrumb from '../../../components/global/breadcrumb/Breadcrumb';
import ImageGroups from '../../../components/imageGroups/ImageGroups';
import ProductDetailLoad from '../../../components/loads/ProductDetailLoad';
import Description from '../../../components/product/description/Description';
import Price from '../../../components/product/price/Price';
import ProductInfo from '../../../components/product/productInfo/ProductInfo';
import Specs from '../../../components/product/specs/Specs';
import WholeSalesPrice from '../../../components/product/wholeSalesPrice/WholeSalesPrice';
import ProductsByCategory from '../../../components/productsByCategory/ProductsByCategory';
import { useImageGroupsByName } from '../../../hooks/useImageGroupsByName';
import { usePageBreakpoints } from '../../../hooks/usePageBreakpoints';
import { useSmartTextColor } from '../../../hooks/useSmartTextColor';
import { useTemplateByPage } from '../../../hooks/useTemplateByPage';
import ComboHeader from './combos/ComboHeader';
import ComboProducts from './combos/ComboProducts';
import { ComboBottomSheet } from './combos/components/ComboBottomSheet';
import { ComboModal } from './combos/components/ComboModal';
import { useProductLogic } from './hooks/useProductLogic';
import { getComboInfo } from './utils/comboUtils';

const TemplateProductLists = lazy(
  () => import('../../../components/templateProductLists/TemplateProductLists'),
);
const SaleForm = lazy(() => import('../../../components/global/saleForm.tsx/SaleForm'));

function Default() {
  const { setMenuOpen, menuOpen, order } = useWebContext();
  const { productId, name } = useParams<{ productId: string; name: string }>();
  const [comboBottomSheetOpen, setComboBottomSheetOpen] = useState<boolean>(false);
  const [comboModalOpen, setComboModalOpen] = useState<boolean>(false);
  const [productVariants, setProductVariants] = useState<Record<string, IFullProduct[]>>({});

  const { device } = useDevice();
  const color = useSmartTextColor();
  const { breakpoints } = usePageBreakpoints();
  const template = useTemplateByPage('product');

  const openBottomSheet = () => {
    setMenuOpen(true);
    setComboBottomSheetOpen(true);
  };

  useEffect(() => {
    if (!menuOpen && comboBottomSheetOpen) {
      setComboBottomSheetOpen(false);
    }
  }, [menuOpen]);

  const openComboDetails = useCallback(
    () => (device === 'desktop' ? setComboModalOpen(true) : openBottomSheet()),
    [device],
  );

  const imageGroupOne = useImageGroupsByName({
    locate: 'image_group_1',
    imageGroups: template?.image_groups,
  });

  const {
    specs,
    chosenSpec,
    cartItem,
    isAvailable,
    isAllAvailable,
    selectChosenFullProduct,
    selectProductsLoading,
    selectProductLoading,
    productsByCategoryFiltered,
    handleChangeSpec,
  } = useProductLogic(productId, name);

  const comboInfo = getComboInfo(selectChosenFullProduct);
  const hasFreeShipping = selectChosenFullProduct?.delivery_discount_data?.type_discount === 'free';

  useEffect(() => {
    if (selectChosenFullProduct?.best_combo) {
      getProductByCombo(order?.shop_id, selectChosenFullProduct?.best_combo?.id).then(
        (products: void | IProduct[]) => {
          if (!products || products.length === 0) return;
          products.map((product: IProduct) =>
            setProductVariants((prev) => ({
              ...prev,
              [product.product_id]: product.full_products,
            })),
          );
        },
      );
    }
  }, [selectChosenFullProduct?.best_combo]);

  return (
    <>
      {['disabled', 'inactive'].includes(isAllAvailable) ? (
        <>
          <div className="d-flex align-items-center justify-content-center p-4">
            <Card className="shadow" style={{ borderRadius: '20px' }}>
              <Card.Body className="text-center p-4">
                <img
                  src={productNotFound}
                  alt="Produto não está disponível"
                  style={{ maxWidth: '20rem' }}
                />
                <h3 className="mt-4">Desculpe, produto não encontrado</h3>
              </Card.Body>
            </Card>
          </div>
        </>
      ) : (
        <div className="ProductDetail">
          <Container className="py-2" fluid>
            <Row className="justify-content-center">
              <Col {...breakpoints} className="breakpoint-page px-0">
                {selectChosenFullProduct && (
                  <Breadcrumb
                    page="product"
                    product={selectChosenFullProduct?.title}
                    categoryId={selectChosenFullProduct.category_id}
                    categoryName={
                      productsByCategoryFiltered[0]?.webapp_name ??
                      productsByCategoryFiltered[0]?.name ??
                      ''
                    }
                  />
                )}

                <Card className="wrapper-card border-0">
                  <Card.Body className="px-0 pt-1 pb-2 px-md-0 py-md-3">
                    <Row className="mx-0">
                      {selectProductLoading ? (
                        <ProductDetailLoad />
                      ) : (
                        <>
                          {selectChosenFullProduct && (
                            <ImagePreview fullProduct={selectChosenFullProduct} />
                          )}

                          {selectChosenFullProduct && (
                            <Col
                              md={5}
                              lg={6}
                              className="py-0 d-flex justify-content-between flex-column"
                            >
                              <Row>
                                <Col xs={12}>
                                  <h1 className="title mb-0">{selectChosenFullProduct.title}</h1>

                                  {cartItem && cartItem.options.length > 0 && (
                                    <ListSelectedOptions options={cartItem.options} />
                                  )}

                                  {selectChosenFullProduct.brand && (
                                    <strong>{selectChosenFullProduct.brand}</strong>
                                  )}

                                  <hr className="mt-0" />

                                  <Description
                                    description={selectChosenFullProduct?.description}
                                    limit
                                  />
                                </Col>
                              </Row>
                              {selectChosenFullProduct?.warnings?.length ? (
                                <Row className="mt-2">
                                  <Col
                                    xs={12}
                                    className="d-flex justify-content-betweens flex-column gap-2"
                                  >
                                    {selectChosenFullProduct.warnings?.map((warning, index) => (
                                      <div className="w-75 warning-wrapper">
                                        <p key={index} className="mb-1">
                                          {Warnings[warning]}
                                        </p>
                                      </div>
                                    )) || null}
                                  </Col>
                                </Row>
                              ) : null}
                              <Specs
                                chosenSpec={chosenSpec}
                                specs={specs}
                                handleChangeSpec={handleChangeSpec}
                              />

                              <Row className="justify-content-end">
                                <Col xs={12}>
                                  <div className="price-wrapper">
                                    {hasFreeShipping &&
                                      selectChosenFullProduct.delivery_discount_data && (
                                        <FreeShippingInLine
                                          classes="align-self-end"
                                          deliveryDiscountData={
                                            selectChosenFullProduct.delivery_discount_data
                                          }
                                        />
                                      )}

                                    <Price
                                      showDiscount
                                      absoluteDiscount
                                      discountType="tag"
                                      discountStyles={{ top: '-37px', right: '0' }}
                                      price={selectChosenFullProduct.price}
                                      defaultPrice={selectChosenFullProduct.default_price}
                                      rewardPrice={
                                        selectChosenFullProduct.alerts?.find(
                                          (alert) => alert.type === 'reward',
                                        )?.price
                                      }
                                    />
                                    <WholeSalesPrice product={selectChosenFullProduct} />
                                    <QuantityAlert
                                      product={selectChosenFullProduct}
                                      isAvailable={isAvailable === 'available'}
                                    />
                                    <Col xs={12} md={7} xl={7} className="p-0">
                                      <Suspense fallback={<div />}>
                                        <SaleForm
                                          fullWidth
                                          quantityInputDirection="end"
                                          product={selectChosenFullProduct}
                                          btnStyles={{ padding: '6px .75rem' }}
                                          currentMeasure={selectChosenFullProduct.measure}
                                          styles={{
                                            position: 'relative',
                                            marginTop: '5px',
                                            padding: '0',
                                            bottom: 0,
                                            right: 0,
                                          }}
                                          isAvailable={isAvailable === 'available'}
                                        />
                                      </Suspense>
                                    </Col>
                                  </div>
                                </Col>
                              </Row>
                            </Col>
                          )}
                        </>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
          <Container className="py-2" fluid>
            <Row className="gap-2 justify-content-md-center px-md-0 px-2 row">
              {selectChosenFullProduct?.best_combo && (
                <Col {...breakpoints} className="px-0 breakpoint-page montserrat combo-wrapper">
                  <ComboHeader comboName={selectChosenFullProduct.best_combo.name} />
                  <ComboProducts
                    products={selectChosenFullProduct.best_combo.main_products}
                    comboInfo={comboInfo}
                    device={device}
                    openComboDetails={openComboDetails}
                  />
                </Col>
              )}
              <Col {...breakpoints} className="px-0 breakpoint-page">
                {productsByCategoryFiltered.length > 0 &&
                  productsByCategoryFiltered[0]?.products &&
                  productsByCategoryFiltered[0].products.length > 0 && (
                    <h5 className="mb-0" style={{ color }}>
                      Veja também!
                    </h5>
                  )}

                {selectProductsLoading === false && (
                  <ProductsByCategory productsGroup={productsByCategoryFiltered} />
                )}
                {template && imageGroupOne.length > 0 && (
                  <ImageGroups imageGroups={imageGroupOne} type="grid" />
                )}

                <ProductInfo product={selectChosenFullProduct} />

                <Suspense fallback={<ListProductsLoad />}>
                  <TemplateProductLists position={1} productList={template?.product_lists} />
                </Suspense>

                <Suspense fallback={<ListProductsLoad />}>
                  <TemplateProductLists position={2} productList={template?.product_lists} />
                </Suspense>
              </Col>
            </Row>
          </Container>

          <ComboBottomSheet
            open={comboBottomSheetOpen}
            onClose={() => setComboBottomSheetOpen(false)}
            product={selectChosenFullProduct}
            variants={productVariants}
          />

          <ComboModal
            show={comboModalOpen}
            onClose={() => setComboModalOpen(false)}
            product={selectChosenFullProduct}
            variants={productVariants}
          />
        </div>
      )}
    </>
  );
}

export default Default;
